import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AcademicCapIcon, UserGroupIcon, TrophyIcon, UserIcon } from '@heroicons/react/24/outline';
import { getApiUrl } from '../../utils/api';
import ImageModal from '../../components/ImageModal';
import { getImageUrlWithEnv } from '../../utils/imageUrl';

const CoachingStaff = () => {
  const [coachingStaff, setCoachingStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const fetchCoachingStaff = async () => {
      try {
        setLoading(true);
        // Fetch all coaching staff data
        const response = await fetch(getApiUrl('/api/coaching-staff/'));
        if (!response.ok) {
          throw new Error('Failed to fetch coaching staff');
        }
        const data = await response.json();
        setCoachingStaff(data);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching coaching staff:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCoachingStaff();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-georgian">მონაცემების ჩატვირთვა...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 font-georgian mb-4">შეცდომა: {error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-orange-600 hover:bg-orange-700 text-white font-bold py-2 px-4 rounded font-georgian"
          >
            ხელახლა ცდა
          </button>
        </div>
      </div>
    );
  }

  // Filter for first team coaches (main staff only)
  const firstTeamCoaches = coachingStaff.filter(coach => 
    coach.team_type === 'main_staff'
  );

  // Custom sorting to ensure specific coaches appear first
  const sortedFirstTeamCoaches = firstTeamCoaches.sort((a, b) => {
    const priorityOrder = ['ზურაბ ლომიძე', 'ზაზა ლაცაბიძე'];
    
    const aIndex = priorityOrder.indexOf(a.name);
    const bIndex = priorityOrder.indexOf(b.name);
    
    // If both are in priority list, sort by priority order
    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }
    
    // If only 'a' is in priority list, 'a' comes first
    if (aIndex !== -1) {
      return -1;
    }
    
    // If only 'b' is in priority list, 'b' comes first
    if (bIndex !== -1) {
      return 1;
    }
    
    // If neither is in priority list, maintain original order
    return 0;
  });

  // Remove youth coaches filtering since they will be shown on separate page

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 font-georgian mb-4">
            სამწვრთნელო შტაბი
          </h1>
          <p className="text-xl text-gray-600 font-georgian max-w-3xl mx-auto">
            ჩვენი გუნდის პროფესიონალური სამწვრთნელო შტაბი
          </p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <AcademicCapIcon className="h-8 w-8 text-primary-600 mx-auto mb-2" />
            <h3 className="text-lg font-semibold text-gray-900 font-georgian">მწვრთნელები</h3>
            <p className="text-2xl font-bold text-primary-600">{coachingStaff.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <TrophyIcon className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
            <h3 className="text-lg font-semibold text-gray-900 font-georgian">საშუალო გამოცდილება</h3>
            <p className="text-2xl font-bold text-yellow-600">{Math.round(coachingStaff.reduce((sum, c) => sum + parseInt(c.experience), 0) / coachingStaff.length)} წელი</p>
          </div>
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <UserGroupIcon className="h-8 w-8 text-green-600 mx-auto mb-2" />
            <h3 className="text-lg font-semibold text-gray-900 font-georgian">პირველი გუნდის მწვრთნელები</h3>
            <p className="text-2xl font-bold text-green-600">{firstTeamCoaches.length}</p>
          </div>
        </div>

        {/* First Team Coaching Staff */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 font-georgian mb-6">
            პირველი გუნდის სამწვრთნელო შტაბი
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {sortedFirstTeamCoaches.map((coach, index) => (
              <div key={index} className="bg-gray-50 rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300 hover:scale-105">
                {/* Coach Photo */}
                <div className="relative h-48 bg-gray-200 cursor-pointer" onClick={() => coach.photo_url && setSelectedImage({
                  imageUrl: getImageUrlWithEnv(coach.photo_url),
                  alt: coach.name,
                  title: coach.name,
                  subtitle: `${coach.position} • ${coach.nationality}`
                })}>
                  {coach.photo_url ? (
                    <img
                      src={getImageUrlWithEnv(coach.photo_url)}
                      alt={coach.name}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div 
                    className={`w-full h-full ${coach.photo_url ? 'hidden' : 'flex'} items-center justify-center bg-gradient-to-br from-primary-500 to-primary-700`}
                  >
                    <UserIcon className="h-16 w-16 text-white" />
                  </div>
                </div>
                
                {/* Coach Info */}
                <div className="p-4">
                  <h3 className="font-bold text-gray-900 font-georgian text-lg mb-1">{coach.name}</h3>
                  <p className="text-sm text-primary-600 font-georgian mb-2">{coach.position}</p>
                  
                  <div className="space-y-1 text-sm text-gray-600 font-georgian">
                    {coach.age && (
                      <p>ასაკი: {coach.age} წელი</p>
                    )}
                    <p>{coach.nationality}</p>
                    {coach.experience && (
                      <p>გამოცდილება: {coach.experience}</p>
                    )}
                    {coach.license && (
                      <p>ლიცენზია: {coach.license}</p>
                    )}
                  </div>
                  
                  {coach.description && (
                    <p className="mt-3 text-xs text-gray-500 font-georgian line-clamp-2">{coach.description}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>


        {/* Navigation */}
        <div className="text-center">
          <Link
            to="/team"
            className="inline-flex items-center bg-primary-600 hover:bg-primary-700 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200 font-georgian"
          >
            ← უკან გუნდის გვერდზე
          </Link>
        </div>
      </div>
      
      {/* Image Modal */}
      <ImageModal
        isOpen={selectedImage !== null}
        onClose={() => setSelectedImage(null)}
        imageUrl={selectedImage?.imageUrl}
        alt={selectedImage?.alt}
        title={selectedImage?.title}
        subtitle={selectedImage?.subtitle}
      />
    </div>
  );
};

export default CoachingStaff;
